/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/panel-type.service.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import AuditEnumValues from './definitions/enums';
import { valuePick } from '@utils-clean/value-pick';
import {
    PanelGraphType,
    PanelType,
    PanelThresholdType,
    PanelSeriesType,
    PanelModelType,
    PanelBenchmark,
    PanelColumnType,
    PanelColumnRangeCategory,
} from 'types/graphql-global-types';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import { removePropertiesFromObject } from 'views/pages/admin/audit-log/service';

const removePropsForModelNegativeBinomial = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'ThresholdType',
        'LineThresholdType',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'StableValue',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'BenchmarkType',
        'BenchmarkValue',
        'PlotBy',
        'RateBy',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly'],
};

const removePropsForModelFunnel = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'ThresholdType',
        'LineThresholdType',
    ],
    ModelParams: [
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'StableValue',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'PlotBy',
        'RateBy',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelFunnelTrend = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'ThresholdType',
        'LineThresholdType',
    ],
    ModelParams: [
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'StableValue',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'PlotBy',
        'RateBy',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'AmberMaxScore',
        'GreenMaxScore',
        'LowerBigRed',
        'UpperBigRed',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelTRI = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'LineThresholdType',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'PlotBy',
        'RateBy',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelTRITrend = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'LineThresholdType',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'PlotBy',
        'RateBy',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'MinimumDataMovementScore',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelPoissonTrend = {
    Graph: [
        'DisplayBy',
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'ThresholdType',
        'LineThresholdType',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
    ],
    DataSource: ['CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelHeatMap = {
    Graph: [
        'DisplayUnits',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'ThresholdNum1',
        'ThresholdNum2',
        'ThresholdType',
        'LineThresholdType',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'CountFirstEventTypeOnly', 'TimePeriod', 'RateByType'],
};

const removePropsForModelColumnStandard = {
    Graph: [
        'DisplayBy',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'LineCalculation',
        'CategoryRangeUnit',
        'Categories',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'TimePeriod', 'RateByType'],
};

const removePropsForDonut = {
    Graph: [
        'DisplayBy',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'LineCalculation',
        'CategoryRangeUnit',
        'Categories',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'DisplayUnits',
        'ColumnOrderBy',
        'XAxisCategory',
        'XAxisDataField',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
        'ThresholdNum1',
        'XAxisCategory',
        'XAxisDataField',
    ],
    DataSource: [
        'UseEarliestEndDate',
        'XAxisEndDateFields',
        'TimePeriod',
        'RateByType',
        'CountFirstEventTypeOnly',
        'XAxisCategory',
        'XAxisDataField',
    ],
};

const removePropsForStackedColumn = {
    Graph: [
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'LineCalculation',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'ColumnRangeCategory',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
        'ColumnType',
        'HideStudyColumns',
        'DisplayUnits',
        'ThresholdNum1',
        'Categories',
        'OrderBy',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
        'ThresholdNum1',
    ],
    DataSource: [
        'UseEarliestEndDate',
        'XAxisEndDateFields',
        'TimePeriod',
        'RateByType',
        'CountFirstEventTypeOnly',
        'SortField',
        'XAxisDataField',
    ],
};

const removePropsForModelColumnNumberRange = {
    Graph: [
        'DisplayBy',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'LineCalculation',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'TimePeriod', 'RateByType', 'CountFirstEventTypeOnly'],
};

const removePropsForModelColumnDateRange = {
    Graph: [
        'DisplayBy',
        'TimePeriod',
        'Series',
        'SeriesType',
        'NumberOfSeries',
        'LineCalculation',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'TimePeriod', 'RateByType', 'CountFirstEventTypeOnly'],
};

const removePropsForModelLineTypeSubject = {
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
        'TimePeriod',
        'XAxisTimePeriod',
    ],
    ModelParams: [
        'HideLower',
        'HideUpper',
        'StableValue',
        'AmberMaxScore',
        'GreenMaxScore',
        'UnitMultiplier',
        'AmberCutoff',
        'RedCutoff',
        'SiteScoring',
        'BenchmarkType',
        'BenchmarkValue',
        'TRIThreshold1Type',
        'TRIThreshold2Type',
        'MinimumDataMovementScore',
        'ThresholdNum1',
        'ThresholdNum2',
        'ConfidenceIntervalType',
        'UpperAmber',
        'UpperBigRed',
        'UpperRed',
        'LowerRed',
        'LowerBigRed',
        'LowerAmber',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'StudyComparison',
        'RateBy',
        'PlotBy',
    ],
    DataSource: ['UseEarliestEndDate', 'XAxisEndDateFields', 'TimePeriod', 'RateByType', 'CountFirstEventTypeOnly'],
};

const removePropsForModelLineTypeSubjectByVisit = {
    ...removePropsForModelLineTypeSubject,
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'LineThresholdType',
        'LineCalculation',
        'TimePeriod',
        'XAxisTimePeriod',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
};

const removePropsForModelLineTypeSite = {
    ...removePropsForModelLineTypeSubject,
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
};

const removePropsForModelLineTypeEvent = {
    ...removePropsForModelLineTypeSubject,
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
};

const removePropsForModelLineTypeSiteCumulative = {
    ...removePropsForModelLineTypeSubject,
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'LineCalculation',
        'LineThresholdType',
        'ThresholdNum1',
        'ThresholdNum2',
        'TimePeriod',
    ],
};

const removePropsForModelLineTypeSiteAverage = {
    ...removePropsForModelLineTypeSubject,
    Graph: [
        'DisplayUnits',
        'DisplayBy',
        'NumberOfSeries',
        'Categories',
        'ColumnType',
        'HideStudyColumns',
        'OrderBy',
        'CategoryRangeUnit',
        'CategoryRangePeriod',
        'DataInclusionUnit',
        'DataInclusionPeriod',
        'ColumnRangeCategory',
        'TimePeriod',
        'ThresholdNum1',
        'ThresholdNum2',
    ],
};

const TRIOpDateFields: string[] = [
    'TRIOpDateParameters',
    'Weight',
    'Decision',
    'IncludeInSiteScoring',
    'Active',
    'ConfigId',
];

const TRIProportionsOTFields: string[] = ['ProportionsOverTimeParameters', 'Active', 'ConfigId', 'AssociatedRisks'];

const timelineFields = ['PanelName', 'Active', 'Timeline', 'ConfigId'] as const;

const EventRatesOTFields: string[] = ['EventRatesOverTimeParameters', 'Active', 'ConfigId', 'AssociatedRisks'];

const includeFields = (record: any, properties: string[]) => {
    Object.keys(record).forEach((key) => {
        if (!properties.includes(key)) {
            record[key] = undefined;
        }
    });

    return record;
};

const removePropsForModelLineByType = (SeriesType: any) => {
    if (AuditEnumValues.GraphSeries[SeriesType] === PanelSeriesType.SITE) {
        return removePropsForModelLineTypeSite.Graph;
    }
    if (AuditEnumValues.GraphSeries[SeriesType] === PanelSeriesType.SUBJECT) {
        return removePropsForModelLineTypeSubject.Graph;
    }
    if (AuditEnumValues.GraphSeries[SeriesType] === PanelSeriesType.SUBJECT_BY_VISIT) {
        return removePropsForModelLineTypeSubjectByVisit.Graph;
    }
    if (AuditEnumValues.GraphSeries[SeriesType] === PanelSeriesType.EVENT) {
        return removePropsForModelLineTypeEvent.Graph;
    }
    if (AuditEnumValues.GraphSeries[SeriesType] === PanelSeriesType.SITE_CUMULATIVE) {
        return removePropsForModelLineTypeSiteCumulative.Graph;
    }
    return removePropsForModelLineTypeSiteAverage.Graph;
};

const addEmptyNewCategories = (len: any, newCategories: any) => {
    for (let i = 0; i < len; i++) {
        newCategories.push([]);
    }
    return newCategories;
};

export const handleAuditLogRecord = ({
    oldItem,
    newItem,
    refactoredOldRecord,
    refactoredNewRecord,
}: {
    oldItem: any;
    newItem: any;
    refactoredOldRecord: any;
    refactoredNewRecord: any;
}) => {
    const panelModel = newItem?.panelType === PanelType.MODEL ? PanelService.getPanelModel(newItem?.modelParams) : null;
    const oldPanelModel =
        oldItem?.panelType === PanelType.MODEL ? PanelService.getPanelModel(oldItem?.modelParams) : null;
    const panelType = newItem ? PanelService.getPanelType(newItem) : undefined;
    const columnType = newItem ? PanelService.getPanelColumnType(newItem) : undefined;
    const columnCategoryType = newItem ? PanelService.getPanelColumnCategoryType(newItem) : undefined;
    const panelSeriesType = newItem ? PanelService.getPanelSeriesType(newItem) : undefined;

    if (
        panelModel === PanelConstants.modelOptions.NEGATIVE_BINOMIAL ||
        panelModel === PanelConstants.modelOptions.RATE_GROUP
    ) {
        if (!oldItem)
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelNegativeBinomial.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelNegativeBinomial.ModelParams,
                    ),
                    UnitMultiplier:
                        refactoredNewRecord.DataSource?.RateByType === 1
                            ? refactoredNewRecord.ModelParams.UnitMultiplier
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelNegativeBinomial.DataSource,
                    ),
                    TimePeriod:
                        refactoredNewRecord.DataSource?.RateByType === 0
                            ? refactoredNewRecord.DataSource.TimePeriod
                            : undefined,
                    UnitMultiplier:
                        // If the new items rate by type is COUNT, we need to display the unit multiplier
                        refactoredNewRecord?.DataSource?.RateByType === 1
                            ? newItem?.modelParams?.unitMultiplier
                            : undefined,
                },
            };
        else {
            const showScores =
                oldPanelModel !== panelModel &&
                oldPanelModel !== PanelModelType.TRI_OPERATIONAL &&
                oldPanelModel !== PanelModelType.FUNNEL;

            refactoredOldRecord = {
                ...refactoredOldRecord,
                IncludeInSiteScoring:
                    showScores || refactoredOldRecord.IncludeInSiteScoring !== undefined
                        ? oldItem?.includeInSiteScoring
                        : null,
                Weight: showScores || refactoredOldRecord.Weight ? oldItem?.weight : null,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    SiteScoring:
                        oldPanelModel !== panelModel
                            ? AuditEnumValues.ModelSiteScoring.indexOf(oldItem?.modelParams.siteScoring)
                            : refactoredOldRecord?.ModelParams?.SiteScoring,
                    UnitMultiplier:
                        // Used to show the unit multiplier if the value has changed while on COUNT type
                        (oldPanelModel !== panelModel ||
                            refactoredOldRecord?.ModelParams?.UnitMultiplier !== undefined) &&
                        refactoredNewRecord?.DataSource?.RateByType === undefined
                            ? refactoredOldRecord?.ModelParams?.UnitMultiplier
                            : undefined,
                    AmberMaxScore:
                        showScores || refactoredOldRecord?.ModelParams?.AmberMaxScore
                            ? oldItem?.modelParams.amberMaxScore
                            : null,
                    GreenMaxScore:
                        showScores || refactoredOldRecord?.ModelParams?.GreenMaxScore
                            ? oldItem?.modelParams.greenMaxScore
                            : null,
                    AmberCutoff:
                        oldPanelModel !== panelModel || refactoredOldRecord?.ModelParams?.AmberCutoff !== undefined
                            ? oldItem?.modelParams.amberCutoff
                            : null,
                    RedCutoff:
                        oldPanelModel !== panelModel || refactoredOldRecord?.ModelParams?.RedCutoff
                            ? oldItem?.modelParams.redCutoff
                            : null,
                },
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    RateByType:
                        oldPanelModel !== panelModel
                            ? AuditEnumValues.DataSourceRateBy.indexOf(oldItem.dataSource.rateByType)
                            : refactoredOldRecord?.DataSource?.RateByType,
                    // Only show this value if its value has changed without the RateByType changing
                    TimePeriod:
                        refactoredOldRecord.DataSource?.TimePeriod !== undefined &&
                        refactoredOldRecord.DataSource?.RateByType === undefined
                            ? refactoredOldRecord.DataSource?.TimePeriod
                            : undefined,
                },
            };

            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring:
                    showScores || refactoredNewRecord.IncludeInSiteScoring !== undefined
                        ? newItem?.includeInSiteScoring
                        : null,
                Weight: showScores || refactoredNewRecord.Weight ? newItem?.weight : null,
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    SiteScoring:
                        oldPanelModel !== panelModel
                            ? AuditEnumValues.ModelSiteScoring.indexOf(newItem?.modelParams.siteScoring)
                            : refactoredNewRecord?.ModelParams?.SiteScoring,
                    UnitMultiplier:
                        // Used to show the unit multiplier if the value has changed while on COUNT type
                        (oldPanelModel !== panelModel ||
                            refactoredNewRecord?.ModelParams?.UnitMultiplier !== undefined) &&
                        refactoredNewRecord?.DataSource?.RateByType === undefined
                            ? newItem?.modelParams?.unitMultiplier
                            : undefined,
                    AmberMaxScore:
                        showScores || refactoredNewRecord?.ModelParams?.AmberMaxScore
                            ? newItem?.modelParams.amberMaxScore
                            : null,
                    GreenMaxScore:
                        showScores || refactoredNewRecord?.ModelParams?.GreenMaxScore
                            ? newItem?.modelParams.greenMaxScore
                            : null,
                    AmberCutoff:
                        oldPanelModel !== panelModel || refactoredNewRecord?.ModelParams?.AmberCutoff !== undefined
                            ? newItem?.modelParams.amberCutoff
                            : null,
                    RedCutoff:
                        oldPanelModel !== panelModel || refactoredNewRecord?.ModelParams?.RedCutoff
                            ? newItem?.modelParams.redCutoff
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    RateByType:
                        oldPanelModel !== panelModel
                            ? AuditEnumValues.DataSourceRateBy.indexOf(newItem.dataSource.rateByType)
                            : refactoredNewRecord?.DataSource?.RateByType,
                    TimePeriod:
                        oldPanelModel !== panelModel ||
                        refactoredNewRecord.DataSource?.RateByType === 0 ||
                        refactoredNewRecord.DataSource?.TimePeriod
                            ? AuditEnumValues.DataSourceTimePeriod.indexOf(newItem?.dataSource.timePeriod)
                            : undefined,
                    UnitMultiplier:
                        // If the new item's rate by type is COUNT, we need to display the unit multiplier
                        // It is done here it guarantees this field gets included in the child changes grid
                        refactoredNewRecord?.DataSource?.RateByType === 1
                            ? newItem?.modelParams?.unitMultiplier
                            : undefined,
                },
            };
        }
    } else if (panelModel === PanelConstants.modelOptions.FUNNEL) {
        if (!oldItem)
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelFunnel.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelFunnel.ModelParams,
                    ),
                    ShowHideThresholds:
                        refactoredNewRecord &&
                        newItem &&
                        (refactoredNewRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredNewRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? newItem.modelParams?.hideUpper
                                ? 0
                                : newItem.modelParams?.hideLower
                                  ? 1
                                  : newItem.modelParams?.hideUpper === false && newItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredNewRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredNewRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredNewRecord?.ModelParams?.BenchmarkValue &&
                            newItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredNewRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(refactoredNewRecord.DataSource, removePropsForModelFunnel.DataSource),
                },
            };
        else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ShowHideThresholds:
                        refactoredOldRecord &&
                        oldItem &&
                        (refactoredOldRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredOldRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? oldItem.modelParams?.hideUpper
                                ? 0
                                : oldItem.modelParams?.hideLower
                                  ? 1
                                  : oldItem.modelParams?.hideUpper === false && oldItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredOldRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredOldRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredOldRecord?.ModelParams?.BenchmarkValue &&
                            oldItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredOldRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ShowHideThresholds:
                        refactoredNewRecord &&
                        newItem &&
                        (refactoredNewRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredNewRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? newItem.modelParams?.hideUpper
                                ? 0
                                : newItem.modelParams?.hideLower
                                  ? 1
                                  : newItem.modelParams?.hideUpper === false && newItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredNewRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredNewRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredNewRecord?.ModelParams?.BenchmarkValue &&
                            newItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredNewRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
            };
        }
    } else if (panelModel === PanelConstants.modelOptions.FUNNEL_TREND) {
        if (!oldItem) {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ShowHideThresholds:
                        refactoredOldRecord &&
                        oldItem &&
                        (refactoredOldRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredOldRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? oldItem.modelParams?.hideUpper
                                ? 0
                                : oldItem.modelParams?.hideLower
                                  ? 1
                                  : oldItem.modelParams?.hideUpper === false && oldItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredOldRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredOldRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredOldRecord?.ModelParams?.BenchmarkValue &&
                            oldItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredOldRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelFunnelTrend.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelFunnelTrend.ModelParams,
                    ),
                    ShowHideThresholds:
                        refactoredNewRecord &&
                        newItem &&
                        (refactoredNewRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredNewRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? newItem.modelParams?.hideUpper
                                ? 0
                                : newItem.modelParams?.hideLower
                                  ? 1
                                  : newItem.modelParams?.hideUpper === false && newItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredNewRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredNewRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredNewRecord?.ModelParams?.BenchmarkValue &&
                            newItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredNewRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelFunnelTrend.DataSource,
                    ),
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ShowHideThresholds:
                        refactoredOldRecord &&
                        oldItem &&
                        (refactoredOldRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredOldRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? oldItem.modelParams?.hideUpper
                                ? 0
                                : oldItem.modelParams?.hideLower
                                  ? 1
                                  : oldItem.modelParams?.hideUpper === false && oldItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredOldRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredOldRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredOldRecord?.ModelParams?.BenchmarkValue &&
                            oldItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredOldRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ShowHideThresholds:
                        refactoredNewRecord &&
                        newItem &&
                        (refactoredNewRecord.ModelParams.hasOwnProperty('HideUpper') ||
                            refactoredNewRecord.ModelParams.hasOwnProperty('HideLower'))
                            ? newItem.modelParams?.hideUpper
                                ? 0
                                : newItem.modelParams?.hideLower
                                  ? 1
                                  : newItem.modelParams?.hideUpper === false && newItem.modelParams?.hideLower === false
                                    ? 2
                                    : undefined
                            : undefined,
                    HideUpper: null,
                    HideLower: null,
                    BenchmarkValue:
                        (oldPanelModel !== panelModel && refactoredNewRecord?.ModelParams?.BenchmarkValue) ||
                        refactoredNewRecord?.ModelParams?.BenchmarkType === 2 ||
                        (refactoredNewRecord?.ModelParams?.BenchmarkValue &&
                            newItem.modelParams.benchmarkType === PanelBenchmark.STUDY_SPECIFIC)
                            ? refactoredNewRecord?.ModelParams?.BenchmarkValue
                            : null,
                },
            };
        }
    } else if (panelModel === PanelConstants.modelOptions.TRI_OPERATIONAL) {
        const showFieldsThresholds =
            oldPanelModel !== panelModel && oldPanelModel !== PanelModelType.TRI_OPERATIONAL_TREND;
        if (!oldItem) {
            const newThresholdType = PanelService.getThresholdType(
                newItem?.modelParams?.triThreshold1Type,
                newItem?.modelParams?.triThreshold2Type,
            );
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelTRI.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ThresholdType:
                        newItem &&
                        (refactoredNewRecord?.ModelParams?.TRIThreshold1Type ||
                            refactoredNewRecord?.ModelParams?.TRIThreshold2Type)
                            ? newThresholdType
                            : null,
                    ...removePropertiesFromObject(refactoredNewRecord.ModelParams, removePropsForModelTRI.ModelParams),
                    ThresholdNum1:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum1
                            : null,
                    ThresholdNum2:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum2
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(refactoredNewRecord.DataSource, removePropsForModelTRI.DataSource),
                },
            };
        } else {
            const oldThresholdType = PanelService.getThresholdType(
                oldItem?.modelParams?.triThreshold1Type,
                oldItem?.modelParams?.triThreshold2Type,
            );
            const newThresholdType = PanelService.getThresholdType(
                newItem?.modelParams?.triThreshold1Type,
                newItem?.modelParams?.triThreshold2Type,
            );
            refactoredOldRecord = {
                ...refactoredOldRecord,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ThresholdType:
                        showFieldsThresholds ||
                        (oldItem &&
                            (refactoredOldRecord?.ModelParams?.TRIThreshold1Type ||
                                refactoredOldRecord?.ModelParams?.TRIThreshold2Type))
                            ? oldThresholdType
                            : null,
                    ThresholdNum1:
                        showFieldsThresholds || oldThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? oldItem?.modelParams?.thresholdNum1
                            : null,
                    ThresholdNum2:
                        showFieldsThresholds || oldThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? oldItem?.modelParams?.thresholdNum2
                            : null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    DataInclusionPeriod: showFieldsThresholds
                        ? AuditEnumValues.ModelDataInclusionPeriod.indexOf(newItem?.modelParams?.dataInclusionPeriod)
                        : refactoredNewRecord?.ModelParams.DataInclusionPeriod,
                    DataInclusionUnit: showFieldsThresholds
                        ? newItem?.modelParams?.dataInclusionUnit
                        : refactoredNewRecord?.ModelParams.DataInclusionUnit,
                    StudyComparison: showFieldsThresholds
                        ? AuditEnumValues.ModelStudyComparison.indexOf(newItem?.modelParams?.studyComparison)
                        : refactoredNewRecord?.ModelParams?.StudyComparison,
                    ThresholdType:
                        showFieldsThresholds ||
                        (newItem &&
                            (refactoredNewRecord?.ModelParams?.TRIThreshold1Type ||
                                refactoredNewRecord?.ModelParams?.TRIThreshold2Type))
                            ? newThresholdType
                            : null,
                    ThresholdNum1:
                        showFieldsThresholds || newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? newItem?.modelParams?.thresholdNum1
                            : null,
                    ThresholdNum2:
                        showFieldsThresholds || newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? newItem?.modelParams?.thresholdNum2
                            : null,
                },
            };
        }
    } else if (panelModel === PanelConstants.modelOptions.TRI_OPERATIONAL_TREND) {
        if (!oldItem) {
            const newThresholdType = PanelService.getThresholdType(
                newItem?.modelParams?.triThreshold1Type,
                newItem?.modelParams?.triThreshold2Type,
            );
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelTRITrend.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ThresholdType:
                        newItem &&
                        (refactoredNewRecord?.ModelParams?.TRIThreshold1Type ||
                            refactoredNewRecord?.ModelParams?.TRIThreshold2Type)
                            ? newThresholdType
                            : null,

                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelTRITrend.ModelParams,
                    ),
                    ThresholdNum1:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum1
                            : null,
                    ThresholdNum2:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum2
                            : null,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelTRITrend.DataSource,
                    ),
                },
            };
        } else {
            const oldThresholdType = PanelService.getThresholdType(
                oldItem?.modelParams?.triThreshold1Type,
                oldItem?.modelParams?.triThreshold2Type,
            );
            refactoredOldRecord = {
                ...refactoredOldRecord,
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ThresholdType:
                        oldItem &&
                        (refactoredOldRecord?.ModelParams?.TRIThreshold1Type ||
                            refactoredOldRecord?.ModelParams?.TRIThreshold2Type)
                            ? oldThresholdType
                            : null,
                    ThresholdNum1:
                        oldThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredOldRecord?.ModelParams?.ThresholdNum1
                            : null,
                    ThresholdNum2:
                        oldThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredOldRecord?.ModelParams?.ThresholdNum2
                            : null,
                },
            };
            const newThresholdType = PanelService.getThresholdType(
                newItem?.modelParams?.triThreshold1Type,
                newItem?.modelParams?.triThreshold2Type,
            );
            refactoredNewRecord = {
                ...refactoredNewRecord,
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ThresholdType:
                        newItem &&
                        (refactoredNewRecord?.ModelParams?.TRIThreshold1Type ||
                            refactoredNewRecord?.ModelParams?.TRIThreshold2Type)
                            ? newThresholdType
                            : null,
                    ThresholdNum1:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum1
                            : null,
                    ThresholdNum2:
                        newThresholdType === PanelThresholdType.STUDY_SPECIFIC
                            ? refactoredNewRecord?.ModelParams?.ThresholdNum2
                            : null,
                },
            };
        }
    } else if (panelModel === PanelConstants.modelOptions.RATE_GROUP_TREND) {
        if (!oldItem) {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisDataFieldStartDate: refactoredOldRecord?.DataSource?.XAxisDataField,
                    XAxisDataField: null,
                    XAxisEndDateUsage:
                        oldPanelModel !== panelModel ||
                        refactoredOldRecord?.DataSource?.UseEarliestEndDate !== undefined
                            ? oldItem?.dataSource?.useEarliestEndDate === true
                                ? 'EARLIEST_DATE'
                                : oldItem?.dataSource?.useEarliestEndDate === false
                                  ? 'LATEST_DATE'
                                  : null
                            : null,
                    UseEarliestEndDate: null,
                },
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    RateByTrend: refactoredOldRecord?.ModelParams?.RateBy,
                    RateBy: undefined,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelPoissonTrend.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelPoissonTrend.ModelParams,
                    ),
                    RateByTrend: refactoredNewRecord?.ModelParams?.RateBy,
                    RateBy: undefined,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisDataFieldStartDate: refactoredNewRecord?.DataSource?.XAxisDataField,
                    XAxisEndDateUsage:
                        oldPanelModel !== panelModel ||
                        refactoredNewRecord?.DataSource?.UseEarliestEndDate !== undefined
                            ? newItem?.dataSource?.useEarliestEndDate === true
                                ? 'EARLIEST_DATE'
                                : newItem?.dataSource?.useEarliestEndDate === false
                                  ? 'LATEST_DATE'
                                  : null
                            : null,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelPoissonTrend.DataSource,
                    ),
                    UseEarliestEndDate: null,
                    XAxisDataField: null,
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisDataFieldStartDate: refactoredOldRecord?.DataSource?.XAxisDataField,
                    XAxisEndDateFields:
                        refactoredNewRecord?.DataSource?.XAxisEndDateFields ||
                        refactoredOldRecord?.DataSource?.xAxisEndDateFields
                            ? oldItem?.dataSource?.xAxisEndDateFields
                            : null,
                    XAxisEndDateUsage:
                        refactoredOldRecord?.DataSource?.UseEarliestEndDate === true
                            ? 'EARLIEST_DATE'
                            : refactoredOldRecord?.DataSource?.UseEarliestEndDate === false
                              ? 'LATEST_DATE'
                              : null,
                    UseEarliestEndDate: null,
                    XAxisDataField: null,
                },
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    RateByTrend: refactoredOldRecord?.ModelParams?.RateBy,
                    RateBy: undefined,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisDataFieldStartDate: refactoredNewRecord?.DataSource?.XAxisDataField,
                    XAxisEndDateFields:
                        refactoredNewRecord?.DataSource?.XAxisEndDateFields ||
                        refactoredOldRecord?.DataSource?.xAxisEndDateFields
                            ? newItem?.dataSource?.xAxisEndDateFields
                            : null,
                    XAxisEndDateUsage:
                        oldPanelModel !== panelModel ||
                        refactoredNewRecord?.DataSource?.UseEarliestEndDate !== undefined
                            ? newItem?.dataSource?.useEarliestEndDate === true
                                ? 'EARLIEST_DATE'
                                : newItem?.dataSource?.useEarliestEndDate === false
                                  ? 'LATEST_DATE'
                                  : null
                            : null,
                    UseEarliestEndDate: null,
                    XAxisDataField: null,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    RateByTrend: refactoredNewRecord?.ModelParams?.RateBy,
                    RateBy: undefined,
                    PlotBy:
                        oldPanelModel !== panelModel || refactoredNewRecord?.ModelParams?.PlotBy !== undefined
                            ? AuditEnumValues.ModelPlotBy.indexOf(newItem?.modelParams?.plotBy)
                            : undefined,
                },
            };
        }
    } else if (panelType === PanelGraphType.HEATMAP) {
        if (!oldItem) {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    XAxisDataField: null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    OrderByHeatmap: refactoredNewRecord.Graph.OrderBy,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelHeatMap.Graph),
                    OrderBy: undefined,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelHeatMap.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelHeatMap.DataSource,
                    ),
                    XAxisDataField: null,
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    XAxisDataField: null,
                },
                Graph: {
                    ...refactoredOldRecord.Graph,
                    OrderByHeatmap: refactoredOldRecord.Graph.OrderBy,
                    OrderBy: undefined,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelHeatMap.DataSource,
                    ),
                    XAxisDataField: null,
                },
                Graph: {
                    ...refactoredNewRecord.Graph,
                    OrderByHeatmap: refactoredNewRecord.Graph.OrderBy,
                    OrderBy: undefined,
                },
            };
        }
    } else if (panelType === PanelGraphType.COLUMN && columnType === PanelColumnType.STANDARD) {
        if (!oldItem) {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ColumnOrderBy: refactoredNewRecord?.Graph?.OrderBy,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelColumnStandard.Graph),
                    OrderBy: undefined,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelColumnStandard.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelColumnStandard.DataSource,
                    ),
                    CalculatedBy: refactoredNewRecord?.DataSource?.SortField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord.Graph,
                    ColumnOrderBy: refactoredOldRecord?.Graph?.OrderBy,
                    OrderBy: undefined,
                    Categories: undefined,
                },
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    CalculatedBy: refactoredOldRecord?.DataSource?.SortField,
                    SortByDate: refactoredOldRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ColumnOrderBy: refactoredNewRecord?.Graph?.OrderBy,
                    OrderBy: undefined,
                    Categories: undefined,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    CalculatedBy: refactoredNewRecord?.DataSource?.SortField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
        }
    } else if (
        panelType === PanelGraphType.COLUMN &&
        columnType === PanelColumnType.RANGED &&
        columnCategoryType === PanelColumnRangeCategory.NUMBER
    ) {
        if (!oldItem) {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.Graph,
                        removePropsForModelColumnNumberRange.Graph,
                    ),
                    OrderBy: undefined,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelColumnNumberRange.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelColumnNumberRange.DataSource,
                    ),
                    CalculatedBy: refactoredNewRecord?.DataSource?.SortField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
        } else {
            const differenceOldNewCategories =
                refactoredOldRecord?.Graph?.Categories?.length - refactoredNewRecord?.Graph?.Categories?.length;
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord.Graph,
                    OrderBy: undefined,
                },
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    SortByDate: refactoredOldRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    OrderBy: undefined,
                    Categories:
                        differenceOldNewCategories > 0
                            ? addEmptyNewCategories(differenceOldNewCategories, refactoredNewRecord.Graph.Categories)
                            : refactoredNewRecord.Graph.Categories,
                    ColumnRangeCategory:
                        refactoredOldRecord?.Graph?.ColumnRangeCategory !== undefined &&
                        refactoredOldRecord?.graph?.columnRangeCategory !== newItem?.graph?.columnRangeCategory
                            ? newItem?.graph?.columnRangeCategory === 'NUMBER'
                                ? 0
                                : 1
                            : undefined,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
        }
    } else if (
        panelType === PanelGraphType.COLUMN &&
        columnType === PanelColumnType.RANGED &&
        columnCategoryType === PanelColumnRangeCategory.DATE
    ) {
        if (!oldItem) {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForModelColumnDateRange.Graph),
                    OrderBy: undefined,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelColumnDateRange.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelColumnDateRange.DataSource,
                    ),
                    CalculatedBy: refactoredNewRecord?.DataSource?.SortField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
            };
        } else {
            const isOldColumnTypeStandard = refactoredOldRecord.Graph.ColumnType === 0;
            const isOldPanelTypeModel = refactoredOldRecord.PanelType === PanelType.MODEL;

            let oldDataInclusionPeriod = refactoredOldRecord.Graph.DataInclusionPeriod;
            let oldDataInclusionUnit = refactoredOldRecord.Graph.DataInclusionUnit;

            if (isOldPanelTypeModel) {
                oldDataInclusionPeriod = refactoredOldRecord.ModelParams.DataInclusionPeriod;
                oldDataInclusionUnit = refactoredOldRecord.ModelParams.DataInclusionUnit;
            } else if (isOldColumnTypeStandard) {
                oldDataInclusionPeriod = undefined;
                oldDataInclusionUnit = undefined;
            }

            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord.Graph,
                    OrderBy: undefined,
                    ColumnRangeCategory: isOldColumnTypeStandard
                        ? undefined
                        : refactoredOldRecord.Graph.ColumnRangeCategory,
                    DataInclusionPeriod: oldDataInclusionPeriod,
                    DataInclusionUnit: oldDataInclusionUnit,
                    CategoryRangePeriod: isOldColumnTypeStandard
                        ? undefined
                        : refactoredOldRecord.Graph.CategoryRangePeriod,
                    CategoryRangeUnit: isOldColumnTypeStandard
                        ? undefined
                        : refactoredOldRecord.Graph.CategoryRangeUnit,
                    ColumnType: isOldPanelTypeModel ? undefined : refactoredOldRecord.Graph.ColumnType,
                },
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    XAxisCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    SortByDate: refactoredOldRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
                ModelParams: {
                    DataInclusionPeriod: undefined,
                    DataInclusionUnit: undefined,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    OrderBy: undefined,
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    SortByDate: refactoredNewRecord?.DataSource?.DateField,
                    DateField: null,
                    XAxisDataField: null,
                    SortField: null,
                },
                ModelParams: {},
            };
        }
    } else if (panelType === PanelGraphType.SCATTER_PLOT_LINE && PanelSeriesType.SUBJECT_BY_VISIT === panelSeriesType) {
        if (!oldItem) {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord?.Graph,
                    TimePeriod: undefined,
                    ThresholdNum1: undefined,
                    ThresholdNum2: undefined,
                    LineCalculation: undefined,
                },
            };
            const graphPropertiesToRemove = removePropsForModelLineByType(refactoredNewRecord.Graph.SeriesType);
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, graphPropertiesToRemove),
                    ThresholdLower: refactoredNewRecord?.Graph?.ThresholdNum1,
                    ThresholdUpper: refactoredNewRecord?.Graph?.ThresholdNum2,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelLineTypeSite.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelLineTypeSite.DataSource,
                    ),
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord?.Graph,
                    XAxisTimePeriod: refactoredOldRecord?.Graph?.TimePeriod,
                    TimePeriod: undefined,
                    ThresholdLower: refactoredOldRecord?.Graph?.ThresholdNum1,
                    ThresholdUpper: refactoredOldRecord?.Graph?.ThresholdNum2,
                    ThresholdNum1: undefined,
                    ThresholdNum2: undefined,
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    XAxisTimePeriod:
                        oldPanelModel !== null && panelModel === null
                            ? AuditEnumValues.GraphTimePeriod.indexOf(oldItem?.graph.timePeriod)
                            : refactoredNewRecord?.Graph?.TimePeriod,
                    NumberOfSeries:
                        (oldPanelModel !== null && panelModel === null) ||
                        AuditEnumValues.GraphSeries[refactoredOldRecord.Graph.SeriesType] === PanelSeriesType.SUBJECT
                            ? newItem?.graph.numberOfSeries
                            : refactoredNewRecord?.Graph?.NumberOfSeries,
                    SeriesType:
                        oldPanelModel !== null && panelModel === null
                            ? AuditEnumValues.GraphSeries.indexOf(oldItem?.graph.seriesType)
                            : refactoredNewRecord?.Graph?.SeriesType,
                    TimePeriod: undefined,
                    ThresholdLower: refactoredNewRecord?.Graph?.ThresholdNum1,
                    ThresholdUpper: refactoredNewRecord?.Graph?.ThresholdNum2,
                    ThresholdNum1: undefined,
                    ThresholdNum2: undefined,
                    LineCalculation: undefined,
                },
            };
        }
    } else if (panelType === PanelGraphType.SCATTER_PLOT_LINE) {
        if (!oldItem) {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord?.Graph,
                    XAxisTimePeriod: refactoredOldRecord?.Graph?.TimePeriod,
                    TimePeriod: undefined,
                },
            };
            const graphPropertiesToRemove = removePropsForModelLineByType(refactoredNewRecord.Graph.SeriesType);
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    XAxisTimePeriod: refactoredNewRecord?.Graph?.TimePeriod,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, graphPropertiesToRemove),
                    ThresholdNum1:
                        refactoredNewRecord.Graph.LineThresholdType === 1
                            ? refactoredNewRecord.Graph.ThresholdNum1
                            : undefined,
                    ThresholdNum2:
                        refactoredNewRecord.Graph.LineThresholdType === 1
                            ? refactoredNewRecord.Graph.ThresholdNum2
                            : undefined,
                    TimePeriod: undefined,
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForModelLineTypeSite.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForModelLineTypeSite.DataSource,
                    ),
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                Graph: {
                    ...refactoredOldRecord?.Graph,
                    XAxisTimePeriod: refactoredOldRecord?.Graph?.TimePeriod,
                    TimePeriod: undefined,
                    Series: refactoredOldRecord?.Graph?.Series?.filter((x: []) => x),
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    XAxisTimePeriod:
                        oldPanelModel !== null && panelModel === null
                            ? AuditEnumValues.GraphTimePeriod.indexOf(oldItem?.graph.timePeriod)
                            : refactoredNewRecord?.Graph?.TimePeriod,
                    NumberOfSeries:
                        (oldPanelModel !== null && panelModel === null) ||
                        AuditEnumValues.GraphSeries[refactoredOldRecord.Graph.SeriesType] === PanelSeriesType.SUBJECT
                            ? newItem?.graph.numberOfSeries
                            : refactoredNewRecord?.Graph?.NumberOfSeries,
                    SeriesType:
                        oldPanelModel !== null && panelModel === null
                            ? AuditEnumValues.GraphSeries.indexOf(oldItem?.graph.seriesType)
                            : refactoredNewRecord?.Graph?.SeriesType,
                    TimePeriod: undefined,
                },
            };
        }
    } else if (panelType === PanelGraphType.DONUT) {
        if (!oldItem) {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForDonut.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(refactoredNewRecord.ModelParams, removePropsForDonut.ModelParams),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    SliceCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(refactoredNewRecord.DataSource, removePropsForDonut.DataSource),
                },
            };
        } else {
            refactoredOldRecord = {
                ...refactoredOldRecord,
                PanelType:
                    refactoredOldRecord.panelType === refactoredNewRecord.panelType
                        ? null
                        : refactoredOldRecord.panelType,
                DataSource: {
                    ...refactoredOldRecord.DataSource,
                    SliceCategory: refactoredOldRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(refactoredOldRecord.DataSource, removePropsForDonut.DataSource),
                },
                Graph: {
                    ...refactoredOldRecord.Graph,
                    ...removePropertiesFromObject(refactoredOldRecord.Graph, removePropsForDonut.Graph),
                },
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ...removePropertiesFromObject(refactoredOldRecord.ModelParams, removePropsForDonut.ModelParams),
                },
            };
            refactoredNewRecord = {
                ...refactoredNewRecord,
                PanelType:
                    refactoredNewRecord.panelType === refactoredOldRecord.panelType
                        ? null
                        : refactoredNewRecord.panelType,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForDonut.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(refactoredNewRecord.ModelParams, removePropsForDonut.ModelParams),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    SliceCategory: refactoredNewRecord?.DataSource?.XAxisDataField,

                    ...removePropertiesFromObject(refactoredNewRecord.DataSource, removePropsForDonut.DataSource),
                },
            };
        }
    } else if (panelType === PanelGraphType.STACKED_COLUMN) {
        if (!oldItem) {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                IncludeInSiteScoring: null,
                Decision: null,
                Weight: null,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ColumnOrder: refactoredNewRecord?.Graph?.OrderBy,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForStackedColumn.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForStackedColumn.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    StackedBy: refactoredNewRecord?.DataSource?.SortField,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForStackedColumn.DataSource,
                    ),
                },
            };
        } else {
            refactoredNewRecord = {
                ...refactoredNewRecord,
                PanelType:
                    refactoredOldRecord.panelType === refactoredNewRecord.panelType
                        ? null
                        : refactoredNewRecord.panelType,
                Graph: {
                    ...refactoredNewRecord.Graph,
                    ColumnOrder: refactoredNewRecord?.Graph?.OrderBy,
                    ...removePropertiesFromObject(refactoredNewRecord.Graph, removePropsForStackedColumn.Graph),
                },
                ModelParams: {
                    ...refactoredNewRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.ModelParams,
                        removePropsForStackedColumn.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    StackedBy: refactoredNewRecord?.DataSource?.SortField,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredNewRecord.DataSource,
                        removePropsForStackedColumn.DataSource,
                    ),
                },
            };
            refactoredOldRecord = {
                ...refactoredOldRecord,
                PanelType:
                    refactoredOldRecord.panelType === refactoredNewRecord.panelType
                        ? null
                        : refactoredNewRecord.panelType,
                Graph: {
                    ...refactoredOldRecord.Graph,
                    ColumnOrder: refactoredOldRecord?.Graph?.OrderBy,
                    ...removePropertiesFromObject(refactoredOldRecord.Graph, removePropsForStackedColumn.Graph),
                },
                ModelParams: {
                    ...refactoredOldRecord.ModelParams,
                    ...removePropertiesFromObject(
                        refactoredOldRecord.ModelParams,
                        removePropsForStackedColumn.ModelParams,
                    ),
                },
                DataSource: {
                    ...refactoredNewRecord.DataSource,
                    StackedBy: refactoredOldRecord?.DataSource?.SortField,
                    XAxisCategory: refactoredNewRecord?.DataSource?.XAxisDataField,
                    ...removePropertiesFromObject(
                        refactoredOldRecord.DataSource,
                        removePropsForStackedColumn.DataSource,
                    ),
                },
            };
        }
    } else if (panelType === PanelType.TRI_OP_DATE) {
        refactoredOldRecord = {
            ...includeFields(refactoredOldRecord, TRIOpDateFields),
            Model: oldItem ? PanelType.TRI_OP_DATE : undefined,
            PlotTitle: oldItem?.name,
        };
        refactoredNewRecord = {
            ...includeFields(refactoredNewRecord, TRIOpDateFields),
            Model: newItem ? PanelType.TRI_OP_DATE : undefined,
            PlotTitle: newItem?.name,
        };
    } else if (panelType === PanelType.PROPORTIONS_OVER_TIME) {
        refactoredOldRecord = {
            ...includeFields(refactoredOldRecord, TRIProportionsOTFields),
            Model: oldItem ? PanelType.PROPORTIONS_OVER_TIME : undefined,
            PlotTitle: oldItem?.name,
        };
        refactoredNewRecord = {
            ...includeFields(refactoredNewRecord, TRIProportionsOTFields),
            Model: newItem ? PanelType.PROPORTIONS_OVER_TIME : undefined,
            PlotTitle: newItem?.name,
        };
    } else if (panelType === PanelType.TIMELINE) {
        refactoredOldRecord = valuePick(refactoredOldRecord, timelineFields);
        refactoredNewRecord = valuePick(refactoredNewRecord, timelineFields);
    } else if (panelType === PanelType.EVENT_RATES_OVER_TIME) {
        refactoredOldRecord = {
            ...includeFields(refactoredOldRecord, EventRatesOTFields),
            Model: oldItem ? PanelType.EVENT_RATES_OVER_TIME : undefined,
            PlotTitle: oldItem?.name,
        };
        refactoredNewRecord = {
            ...includeFields(refactoredNewRecord, EventRatesOTFields),
            Model: newItem ? PanelType.EVENT_RATES_OVER_TIME : undefined,
            PlotTitle: newItem?.name,
        };
    }

    return {
        refactoredOldRecord,
        refactoredNewRecord,
    };
};
